import { doc, getDoc } from "firebase/firestore";
import { SyncStatus } from "../../Resources/Constants";
import { makeAutoObservable } from "mobx";
import db from "../Firestore/FirestoreConfig";
import NetworkManager from "./NetworkManager";

export class ClientSyncStatusManager {
  hasChangesOffline = false;
  fetchCompetitionErrorIDs = [];
  networkManager = NetworkManager.getInstance();

  operations = {
    performTransaction: null,

    listenerBlocks: null,
    listenerCompetition: null,
    listenerForms: null,
    listenerChat: null,
    // listenerArrays: null,

    fetchArrays: null,
    fetchForms: null,
    fetchBlocks: null,
    fetchCompetition: null,

    fetchNutritionPlans: null,
  };

  constructor(client) {
    this.client = client;
    makeAutoObservable(this);
  }

  /**
   * Called when a client is unloaded from the in-memory window (see CLIENT_MEMORY_WINDOW.md).
   * Resets operation statuses related to fetch/listener lifecycle so the client doesn't keep
   * stale WAITING/ERROR/SYNCED markers after its fetched state has been cleared.
   */
  resetAfterClientUnload({
    resetFetchArrays = false,
    resetFetchForms = false,
    resetFetchBlocks = false,
    resetFetchNutritionPlans = false,
    removedListenerBlocks = false,
    removedListenerForms = false,
    removedListenerChat = false,
    removedListenerCompetition = false,
  } = {}) {
    if (resetFetchArrays) this.operations.fetchArrays = null;
    if (resetFetchForms) this.operations.fetchForms = null;
    if (resetFetchBlocks) this.operations.fetchBlocks = null;
    if (resetFetchNutritionPlans) this.operations.fetchNutritionPlans = null;

    if (removedListenerBlocks) this.operations.listenerBlocks = null;
    if (removedListenerForms) this.operations.listenerForms = null;
    if (removedListenerChat) this.operations.listenerChat = null;
    if (removedListenerCompetition) this.operations.listenerCompetition = null;
  }

  startOperation(name) {
    if (!(name in this.operations)) {
      console.warn(`Operation "${name}" is not a recognized operation.`);
      return;
    }
    this.operations[name] = SyncStatus.WAITING; // use constant for consistency
    console.log("Operation started:", name);
  }

  /** Mark an individual operation as completed (success or failure) */
  endOperationSuccess(name) {
    if (!(name in this.operations)) {
      console.warn(`Operation "${name}" is not a recognized operation.`);
      return;
    }
    this.operations[name] = SyncStatus.SYNCED;
  }
  endOperationError(name) {
    if (!(name in this.operations)) {
      console.warn(`Operation "${name}" is not a recognized operation.`);
      return;
    }
    this.operations[name] = SyncStatus.ERROR;
  }

  get syncStatus() {
    if (Object.values(this.operations).includes(SyncStatus.WAITING)) {
      return SyncStatus.WAITING;
    } else if (Object.values(this.operations).includes(SyncStatus.ERROR)) {
      return SyncStatus.ERROR;
    } else {
      return SyncStatus.SYNCED;
    }
  }

  /** Get failed operations for retry */
  getFailedOperations() {
    return Object.entries(this.operations)
      .filter(([_, status]) => status === SyncStatus.ERROR)
      .map(([key]) => key);
  }

  async resolveErrors() {
    console.log("Executing resolveErrors...");

    const operationHandlers = {
      listenerBlocks: async () => {
        if (
          this.client.clientTransactionData.length === 0 &&
          this.client.clientDeletionData.length === 0 &&
          this.client.clientDeletionWorkouts.length === 0
        ) {
          await this.client.firestoreListener.setupBlocksListener();
        }
      },
      listenerCompetition: async () => {
        if (
          this.client?.coach?.selectedClientComp?.selectedCompetition != null &&
          this.client?.coach?.showingCoachView === "CoachCompetitions"
        ) {
          await this.client.firestoreListener.setupCompetitionsListener(
            this.client?.coach?.selectedClientComp?.selectedCompetition.id
          );
        }
      },
      listenerForms: async () => {
        await this.client.firestoreListener.setupFormsListener();
      },
      listenerChat: async () => {
        await this.client.firestoreListener.setupChatListener();
      },

      fetchArrays: async () => {
        if (
          this.client.clientTransactionNotes.length === 0 &&
          this.client.clientDeletionNotes.length === 0
        ) {
          this.client.setProperty("tracs", []);
          this.client.setProperty("bodyweights", []);
          this.client.setProperty("notesArray", []);
          await this.client.fetchArraysFromCloud();
        }
      },
      fetchNutritionPlans: async () => {
        if (
          this.client.clientTransactionNutritionPlans.length === 0 &&
          this.client.clientDeletionNutritionPlans.length === 0
        ) {
          this.client.setProperty("nutritionPlans", []);
          await this.client.fetchNutritionPlansFromCloud();
        }
      },
      fetchForms: async () => {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

        const filteredForms = this.client
          .getProperty("forms")
          .filter((form) => new Date(form.last_update) <= oneWeekAgo);

        this.client.setProperty("forms", filteredForms);

        await this.client.fetchFormsFromCloud();
      },
      fetchBlocks: async () => {
        if (
          this.client.clientTransactionData.length === 0 &&
          this.client.clientDeletionData.length === 0 &&
          this.client.clientDeletionWorkouts.length === 0
        ) {
          this.client.setProperty("blocks", []);
          await this.client.fetchBlocksFromCloud();
        }
      },
      fetchCompetition: async () => {
        for (const compId of this.fetchCompetitionErrorIDs) {
          const competitionDocRef = doc(
            db,
            "users",
            this.client.id,
            "competitions",
            compId
          );

          try {
            console.log("Fetching competition document from Firestore...");
            const competitionSnapshot = await getDoc(competitionDocRef);

            if (competitionSnapshot.exists()) {
              await this.client.fetchCompetition(competitionSnapshot.data());
            }
          } catch (error) {
            throw error;
          }
        }
      },
      performTransaction: async () => {
        await this.client.performTransaction();
      },
    };

    for (const operation of this.getFailedOperations()) {
      console.log(`Retrying ${operation}...`);

      if (operationHandlers[operation]) {
        await this.safeOperation(operation, operationHandlers[operation], true);
      } else {
        console.warn(`No handler found for operation: ${operation}`);
      }
    }
  }

  getErrorToastContent(operationName) {
    const errorMessages = {
      performTransaction: {
        title: "clientSyncErrorTransaction-string",
        description: "clientSyncErrorTransactionDescription-string",
      },
      listenerBlocks: {
        title: "clientSyncErrorBlocks-string",
        description: "clientSyncErrorBlocksDescription-string",
      },
      listenerCompetition: {
        title: "clientSyncErrorCompetition-string",
        description: "clientSyncErrorCompetitionDescription-string",
      },
      listenerForms: {
        title: "clientSyncErrorForms-string",
        description: "clientSyncErrorFormsDescription-string",
      },
      listenerChat: {
        title: "clientSyncErrorChat-string",
        description: "clientSyncErrorChatDescription-string",
      },
      fetchArrays: {
        title: "clientSyncErrorArrays-string",
        description: "clientSyncErrorArraysDescription-string",
      },
      fetchNutritionPlans: {
        title: "clientSyncErrorNutritionPlans-string",
        description: "clientSyncErrorNutritionPlansDescription-string",
      },
      fetchForms: {
        title: "clientSyncErrorForms-string",
        description: "clientSyncErrorFormsDescription-string",
      },
      fetchBlocks: {
        title: "clientSyncErrorBlocks-string",
        description: "clientSyncErrorBlocksDescription-string",
      },
      fetchCompetition: {
        title: "clientSyncErrorCompetition-string",
        description: "clientSyncErrorCompetitionDescription-string",
      },
    };

    const content = errorMessages[operationName] || {
      title: "clientSyncError-string",
      description: "clientSyncErrorGenericDescription-string",
    };

    // Return content with variables including client email
    return {
      ...content,
      variables: {
        clientEmail: this.client?.email || "unknown",
      },
    };
  }

  async safeOperation(operationName, operationFn, resolvingErrors = false) {
    if (
      (this.operations[operationName] === SyncStatus.SYNCED &&
        !operationName.toLowerCase().includes("listener") &&
        !["fetchCompetition", "performTransaction"].includes(operationName)) ||
      (["listenerForms"].includes(operationName) &&
        this.operations[operationName] === SyncStatus.SYNCED)
    ) {
      console.log(`${operationName} is already synced. Skipping execution.`);
      return;
    }

    if (this.networkManager.isOffline) {
      console.log(
        `Cannot execute ${operationName} while offline. Skipping execution.`
      );
      // Object.keys(this.operations).forEach((key) => {
      //   this.operations[key] = SyncStatus.ERROR;
      // });
      return;
    }

    try {
      this.startOperation(operationName);
      const result = await operationFn();

      if (operationName === "fetchCompetition" && result && result.id) {
        const compId = result.id;

        const index = this.fetchCompetitionErrorIDs.indexOf(compId);
        if (index !== -1) {
          this.fetchCompetitionErrorIDs.splice(index, 1);
        }
      }
      this.endOperationSuccess(operationName);

      return result;
    } catch (error) {
      console.error(`${operationName} failed:`, error);
      if (
        operationName === "fetchCompetition" &&
        error?.competitionId &&
        !this.fetchCompetitionErrorIDs.includes(error.competitionId.toString())
      ) {
        this.fetchCompetitionErrorIDs.push(error.competitionId.toString());
        console.error(`Competition ID that failed: ${error.competitionId}`);
      }

      // Show error toast if showToast is available
      if (this.showToast && typeof this.showToast === "function") {
        const toastContent = this.getErrorToastContent(operationName);
        this.showToast(toastContent.title, {
          type: "error",
          description: toastContent.description,
          variables: toastContent.variables,
        });
      }

      this.endOperationError(operationName);

      return;
    }
  }

  setOperationStatus(name, status) {
    if (!(name in this.operations)) {
      console.warn(`Operation "${name}" is not a recognized operation.`);
      return;
    }
    this.operations[name] = status;
    // this.updateGlobalStatus();
  }

  clearOfflineOperations() {
    // the list of operation‐keys we want to check+clear
    const toCheck = [
      "performTransaction",
      "listenerCompetition",
      "listenerForms",
      "listenerChat",
      "fetchArrays",
      "fetchForms",
      "fetchBlocks",
      "fetchCompetition",
      "listenerBlocks",
      "fetchNutritionPlans",
    ];

    toCheck.forEach((key) => {
      if (this.operations[key] === SyncStatus.ERROR) {
        this.operations[key] = null;
      }
    });
  }

  get showToast() {
    return this.client?.coach?.showToast;
  }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
      console.log(`Property ${propertyName} set to ${value}`);
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }
}
